import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { PromptCard } from '@/components/quickWrite/PromptCard'
import { QuickWriteEditor } from '@/components/quickWrite/QuickWriteEditor'
import { getDailyPrompt, getRandomPrompt } from '@/data/quickWritePrompts'
import { saveQuickWrite } from '@/services/quickWriteService'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'

export function QuickWrite() {
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const [prompt, setPrompt] = useState(() => getDailyPrompt())

  const handleSwap = useCallback(
    () => setPrompt((current) => getRandomPrompt(current.id)),
    []
  )

  const childId = activeWriter?.id

  const handleSave = useCallback(
    async (content: string) => {
      if (!childId) {
        showToast('Pick a writer first.', 'error')
        return
      }
      try {
        const result = await saveQuickWrite({
          childId,
          promptId: prompt.id,
          content,
        })
        if (result === null) {
          showToast('Saved locally.', 'success')
        } else {
          showToast('Saved. You can come back to this anytime.', 'success')
        }
        navigate('/dashboard')
      } catch (err) {
        console.error('Failed to save quick write', err)
        showToast('Could not save right now. Try again in a moment.', 'error')
      }
    },
    [childId, prompt.id, navigate, showToast]
  )

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-primary)] mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to dashboard
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card>
          <CardContent className="p-6 sm:p-8">
            <PromptCard prompt={prompt} onSwap={handleSwap} />
            <QuickWriteEditor onSave={handleSave} />
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
